import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Minus, Check, Package, Shield, Truck, FileText, Wrench, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductImageCarousel from '../components/ProductImageCarousel';
import ReviewCard from '../components/ReviewCard';
import ReviewForm from '../components/ReviewForm';
import { productsAPI, reviewsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useSEO } from '../hooks/useSEO';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [productReviews, setProductReviews] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productData = await productsAPI.getById(id);
        setProduct(productData);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!product) return;
    reviewsAPI.getAll({ product_id: id }).then(setProductReviews).catch(() => {});
  }, [id, product]);

  useSEO({
    title: product
      ? `${product.name} — ${product.make} ${product.model}`
      : 'Tuning Module',
    description: product
      ? `BlueSpark ${product.name} for ${product.make} ${product.model}. ${product.stockPower ? `Stock: ${product.stockPower}PS` : ''} ${product.tunedPower ? `→ Tuned: ${product.tunedPower}PS` : ''}. Plug-and-play, 2-year warranty, free AU shipping.`
      : 'Performance tuning module from BlueSpark Australia.',
    canonical: product ? `/products/${product.id}` : null,
    image: product?.image || undefined,
    type: 'product',
    jsonLd: product ? (() => {
      const base = 'https://bluesparkautomotive.com.au';
      const productUrl = `${base}/products/${product.id}`;
      const hasReviews = (product.reviewCount || 0) >= 1;
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description || `${product.name} for ${product.make} ${product.model}`,
        image: product.image,
        url: productUrl,
        brand: { '@type': 'Brand', name: 'BlueSpark' },
        sku: product.sku || product.id,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: product.price,
          url: productUrl,
          availability: product.inStock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          seller: { '@type': 'Organization', name: 'BlueSpark Australia' },
        },
      };
      // Only include AggregateRating when we actually have a review — otherwise
      // Google flags it as invalid structured data.
      if (hasReviews && product.averageRating) {
        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: product.averageRating,
          reviewCount: product.reviewCount,
        };
      }
      const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: base },
          {
            '@type': 'ListItem',
            position: 2,
            name: product.category || 'Products',
            item: product.category
              ? `${base}/products?category=${encodeURIComponent(product.category)}`
              : `${base}/products`,
          },
          { '@type': 'ListItem', position: 3, name: product.name, item: productUrl },
        ],
      };
      return [productSchema, breadcrumb];
    })() : null,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-xl">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
            <Link to="/products">
              <Button>Back to Products</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <Link to="/products" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="sticky top-24">
                <ProductImageCarousel
                  customImages={[product.image, ...(product.detailImages || [])].filter(Boolean)}
                />
              </div>
            </div>

            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{product.name}</h1>
              
              <div className="flex items-baseline space-x-4 mb-6">
                <span className="text-4xl font-bold text-blue-600">
                  ${product.price.toFixed(2)} {product.currency}
                </span>
                {product.inStock ? (
                  <span className="text-green-600 font-medium">In Stock</span>
                ) : (
                  <span className="text-red-600 font-medium">Out of Stock</span>
                )}
              </div>

              <p className="text-gray-700 mb-6 text-lg">{product.description}</p>

              <Card className="mb-6">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Performance Gains</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Stock Power</p>
                      <p className="text-2xl font-bold">{product.stockPower}PS</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Power Gain</p>
                      <p className="text-2xl font-bold text-green-600">+{product.gainPower}PS</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Stock Torque</p>
                      <p className="text-2xl font-bold">{product.stockTorque}Nm</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Torque Gain</p>
                      <p className="text-2xl font-bold text-green-600">+{product.gainTorque}Nm</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Installation Instructions */}
              {product.instructionsUrl && product.instructionsUrl !== 'nan' && (
                <Card className="mb-6 border-blue-200 bg-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Wrench className="h-5 w-5 mr-2 text-blue-600" />
                      Installation Instructions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {product.connections && (
                          <div className="bg-white p-3 rounded">
                            <p className="text-gray-600 text-xs">Connection Type</p>
                            <p className="font-semibold">{product.connections}</p>
                          </div>
                        )}
                        {product.railMap && product.railMap !== 'nan' && product.railMap !== '0' && (
                          <div className="bg-white p-3 rounded">
                            <p className="text-gray-600 text-xs">Rail/MAP</p>
                            <p className="font-semibold">{product.railMap}</p>
                          </div>
                        )}
                        {product.boost && product.boost !== 'nan' && product.boost !== '0' && (
                          <div className="bg-white p-3 rounded">
                            <p className="text-gray-600 text-xs">Boost</p>
                            <p className="font-semibold">{product.boost}</p>
                          </div>
                        )}
                        {product.cam && product.cam !== 'nan' && product.cam !== '0' && (
                          <div className="bg-white p-3 rounded">
                            <p className="text-gray-600 text-xs">CAM</p>
                            <p className="font-semibold">{product.cam}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <a
                          href={product.instructionsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full"
                        >
                          <Button className="w-full" variant="default">
                            <FileText className="mr-2 h-4 w-4" />
                            Download Installation PDF
                          </Button>
                        </a>
                      </div>
                      <p className="text-sm text-gray-700">
                        <strong>Installation Time:</strong> 5-30 minutes • <strong>DIY Friendly</strong>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-bold mb-3">Vehicle Specifications</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Make</p>
                    <p className="font-semibold">{product.make}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Model</p>
                    <p className="font-semibold">{product.model}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">{product.engine ? 'Engine' : 'Fuel System'}</p>
                    <p className="font-semibold">{product.engine || product.fuelSystem || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Capacity</p>
                    <p className="font-semibold">{product.cc ? `${product.cc}cc` : '—'}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4 mb-6">
                <span className="font-medium">Quantity:</span>
                <div className="flex items-center border rounded-lg">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-6 font-semibold">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(10, quantity + 1))}
                    disabled={quantity >= 10}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full mb-3"
                onClick={handleAddToCart}
                disabled={!product.inStock}
              >
                Add to Cart - ${(product.price * quantity).toFixed(2)} {product.currency}
              </Button>

              <div className="mb-6 text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-md px-3 py-2 flex items-start gap-2" data-testid="bluetooth-option-hint">
                <span className="text-blue-600 font-bold">+</span>
                <span>Optional <strong>Bluetooth Connectivity</strong> add-on available at checkout (+$99 AUD) — wireless app control for power, torque & throttle response.</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center space-x-2 text-sm">
                  <Truck className="h-5 w-5 text-blue-600" />
                  <span>Fast Shipping</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <span>2 Year Warranty</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Package className="h-5 w-5 text-blue-600" />
                  <span>Easy Returns</span>
                </div>
              </div>

              <Tabs defaultValue="features" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="features" className="flex-1">Features</TabsTrigger>
                  <TabsTrigger value="installation" className="flex-1">Installation</TabsTrigger>
                  <TabsTrigger value="warranty" className="flex-1">Warranty</TabsTrigger>
                </TabsList>
                <TabsContent value="features" className="mt-4">
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
                <TabsContent value="installation" className="mt-4">
                  <div className="space-y-3">
                    <p>Our tuning modules feature plug-and-play installation that takes just 5-30 minutes.</p>
                    <p className="font-semibold">Installation Steps:</p>
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Locate the sensor connectors (MAP/Boost/Pressure sensors)</li>
                      <li>Disconnect the factory connector</li>
                      <li>Connect the BlueSpark harness in between</li>
                      <li>Secure the module in a safe location</li>
                      <li>Start the engine and test</li>
                    </ol>
                    <p className="text-sm text-gray-600 mt-4">Detailed instructions included with every module.</p>
                  </div>
                </TabsContent>
                <TabsContent value="warranty" className="mt-4">
                  <div className="space-y-3">
                    <p className="font-semibold">2-Year Warranty Coverage</p>
                    <p>All BlueSpark tuning modules come with a comprehensive 2-year warranty covering manufacturing defects and component failures.</p>
                    <p className="font-semibold mt-4">30-Day Money Back Guarantee</p>
                    <p>Not satisfied? Return your module within 30 days for a full refund, no questions asked.</p>
                    <p className="text-sm text-gray-600 mt-4">Our modules are completely traceless once removed and won't affect your vehicle warranty.</p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Customer Reviews */}
          <section className="mt-16 border-t pt-12" data-testid="product-reviews-section">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-6 w-6 text-amber-400 fill-amber-400" />
              <h2 className="text-2xl md:text-3xl font-bold">Customer Reviews</h2>
              <span className="text-gray-500 text-sm">
                ({productReviews.length})
              </span>
            </div>

            {productReviews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                {productReviews.slice(0, 6).map((r) => (
                  <ReviewCard key={r.id} review={r} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-10">No reviews yet for this product. Be the first to leave one!</p>
            )}

            <div className="max-w-3xl">
              <ReviewForm
                defaultVehicle={product.name}
                defaultProductImage={product.image}
                productId={id}
                onSubmitted={(r) => setProductReviews((prev) => [r, ...prev])}
              />
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
